import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { asArray } from "@/lib/arrays";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Trophy, Star, Zap, Target, Award, BookOpen, PlayCircle, BarChart3,
  TrendingUp, Users, Calendar, Shield, DollarSign, Gem, Flame,
  Megaphone, Lock, Crown, ChevronRight
} from "lucide-react";

const ICON_MAP = {
  "star": Star, "book": BookOpen, "play": PlayCircle, "award": Award,
  "trending-up": TrendingUp, "bar-chart": BarChart3, "trophy": Trophy,
  "flame": Flame, "zap": Zap, "users": Users, "megaphone": Megaphone,
  "calendar": Calendar, "target": Target, "shield": Shield,
  "dollar": DollarSign, "gem": Gem
};

const LeaderboardPage = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("xp");
  const [xpBoard, setXpBoard] = useState([]);
  const [tradeBoard, setTradeBoard] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [xpData, setXpData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      // 1. Silent sync check first to prevent XP jumping race condition
      try {
        await axios.post(`${API}/gamification/check`, {}, { withCredentials: true });
      } catch (_) {
        // non-blocking
      }

      // 2. Fetch fresh synchronized gamification & leaderboard data
      const [xpRes, tradeRes, achRes, xpDataRes] = await Promise.all([
        axios.get(`${API}/gamification/leaderboard`, { withCredentials: true }),
        axios.get(`${API}/journal/leaderboard`, { withCredentials: true }),
        axios.get(`${API}/gamification/achievements`, { withCredentials: true }),
        axios.get(`${API}/gamification/xp`, { withCredentials: true }),
      ]);
      setXpBoard(asArray(xpRes.data, "leaderboard"));
      setTradeBoard(asArray(tradeRes.data, "leaderboard"));
      setAchievements(asArray(achRes.data, "achievements"));
      setXpData(xpDataRes.data);
    } catch (err) {
      console.error("Error fetching leaderboard data:", err); // eslint-disable-line no-console
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <DashboardLayout title="Leaderboard">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-[#00FF88] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const earned = achievements.filter(a => a.earned);
  const locked = achievements.filter(a => !a.earned);

  return (
    <DashboardLayout title="Leaderboard & Achievements">
      <div className="space-y-5" data-testid="leaderboard-page">

        {/* XP Progress Card */}
        {xpData && (
          <div className="terminal-card rounded-lg p-6" data-testid="xp-progress">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FFCC00]/20 to-[#FF9500]/20 flex items-center justify-center">
                  <Crown className="w-6 h-6 text-[#FFCC00]" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white font-outfit">Level {xpData.level}: {xpData.level_name}</h2>
                  <p className="text-xs text-gray-400 dark:text-[#52525B] font-plex">{xpData.total_xp} XP total</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-[#FFCC00] font-jetbrains">{xpData.achievements_earned}</p>
                <p className="text-[10px] text-gray-400 dark:text-[#52525B] font-jetbrains">/ {xpData.achievements_total} badges</p>
              </div>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500 dark:text-[#A1A1AA] font-plex">{xpData.xp_in_level} / {xpData.xp_for_next_level} XP to Level {xpData.level + 1}</span>
                <span className="text-[#FFCC00] font-jetbrains">{xpData.progress_percentage}%</span>
              </div>
              <div className="w-full h-2.5 bg-white/[0.04] rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#FFCC00] to-[#FF9500] rounded-full transition-all" style={{ width: `${xpData.progress_percentage}%` }} />
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1">
          {[
            { key: "xp", label: "XP Leaderboard", icon: Crown },
            { key: "trades", label: "Top Traders", icon: TrendingUp },
            { key: "badges", label: "Achievements", icon: Award }
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-plex transition-all ${tab === t.key ? "bg-white/[0.06] text-gray-900 dark:text-white border border-gray-200 dark:border-white/10" : "text-[#52525B] hover:text-[#A1A1AA]"}`}
              data-testid={`tab-${t.key}`}>
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
        </div>

        {/* XP Leaderboard */}
        {tab === "xp" && (
          <div className="terminal-card rounded-lg overflow-hidden" data-testid="xp-leaderboard">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-white/[0.06]">
              <p className="font-jetbrains text-xs tracking-[0.15em] text-[#FFCC00] uppercase">XP RANKINGS</p>
            </div>
            {xpBoard.length === 0 ? (
              <div className="p-12 text-center">
                <Crown className="w-10 h-10 text-gray-400 dark:text-[#52525B] mx-auto mb-3" />
                <p className="text-gray-400 dark:text-[#52525B] font-plex">No rankings yet. Complete achievements to earn XP!</p>
              </div>
            ) : (
              <div className="divide-y divide-white/[0.03]">
                {xpBoard.map((entry) => {
                  const isTop3 = entry.rank <= 3;
                  const rankColors = { 1: "#FFCC00", 2: "#C0C0C0", 3: "#CD7F32" };
                  return (
                    <div key={entry.rank} className={`flex items-center gap-4 p-4 ${isTop3 ? "bg-gray-50 dark:bg-white/[0.02]" : ""} hover:bg-gray-50 dark:bg-white/[0.02] transition-colors`} data-testid={`xp-rank-${entry.rank}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-jetbrains font-bold text-sm ${isTop3 ? "" : "bg-white/[0.04] text-[#52525B]"}`}
                        style={isTop3 ? { backgroundColor: `${rankColors[entry.rank]}20`, color: rankColors[entry.rank] } : {}}>
                        {isTop3 ? <Trophy className="w-4 h-4" /> : entry.rank}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-outfit font-semibold text-gray-900 dark:text-white">
                            {entry.user_id === user?.user_id && user?.name ? user.name : (entry.name && entry.name !== "Trader" ? entry.name : (user?.name || "Trader"))}
                          </p>
                          {entry.user_id === user?.user_id && (
                            <span className="text-[10px] font-mono font-bold px-1.5 py-0.2 rounded bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">YOU</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 dark:text-[#52525B] font-plex">Level {entry.level} | {entry.achievements} badges</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold font-jetbrains text-[#FFCC00]">{entry.total_xp}</p>
                        <p className="text-[10px] text-gray-400 dark:text-[#52525B] font-jetbrains">XP</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Trade Leaderboard */}
        {tab === "trades" && (
          <div className="terminal-card rounded-lg overflow-hidden" data-testid="trade-leaderboard">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-white/[0.06]">
              <p className="font-jetbrains text-xs tracking-[0.15em] text-[#00FF88] uppercase">TOP TRADERS BY P&L</p>
            </div>
            {tradeBoard.length === 0 ? (
              <div className="p-12 text-center">
                <TrendingUp className="w-10 h-10 text-gray-400 dark:text-[#52525B] mx-auto mb-3" />
                <p className="text-gray-400 dark:text-[#52525B] font-plex">No traders on the board yet</p>
              </div>
            ) : (
              <div className="divide-y divide-white/[0.03]">
                {tradeBoard.map((entry) => {
                  const isTop3 = entry.rank <= 3;
                  return (
                    <div key={entry.rank} className={`flex items-center gap-4 p-4 ${isTop3 ? "bg-gray-50 dark:bg-white/[0.02]" : ""} hover:bg-gray-50 dark:bg-white/[0.02] transition-colors`} data-testid={`trade-rank-${entry.rank}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-jetbrains font-bold text-sm ${entry.rank === 1 ? "bg-[#FFCC00]/20 text-[#FFCC00]" : entry.rank === 2 ? "bg-gray-400/20 text-gray-300" : entry.rank === 3 ? "bg-orange-700/20 text-orange-400" : "bg-white/[0.04] text-[#52525B]"}`}>
                        {isTop3 ? <Trophy className="w-4 h-4" /> : entry.rank}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-outfit font-semibold text-gray-900 dark:text-white">
                            {entry.user_id === user?.user_id && user?.name ? user.name : (entry.name && entry.name !== "Trader" ? entry.name : (user?.name || "Trader"))}
                          </p>
                          {entry.user_id === user?.user_id && (
                            <span className="text-[10px] font-mono font-bold px-1.5 py-0.2 rounded bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">YOU</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 dark:text-[#52525B] font-plex">{entry.total_wins} wins | Avg R:R {entry.avg_rr} | Best: {entry.best_stock}</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-bold font-jetbrains ${entry.total_pnl >= 0 ? "text-[#00FF88]" : "text-[#FF3B30]"}`}>
                          {entry.total_pnl >= 0 ? "+" : ""}₹{entry.total_pnl.toLocaleString()}
                        </p>
                        <p className="text-[10px] text-gray-400 dark:text-[#52525B] font-jetbrains">P&L</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Achievements */}
        {tab === "badges" && (
          <div className="space-y-5">
            {/* Earned */}
            {earned.length > 0 && (
              <div>
                <p className="font-jetbrains text-[10px] tracking-[0.2em] text-[#00FF88] uppercase mb-3">EARNED ({earned.length})</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="earned-achievements">
                  {earned.map(ach => {
                    const Icon = ICON_MAP[ach.icon] || Star;
                    return (
                      <div key={ach.id} className="terminal-card rounded-lg p-4 border-[#00FF88]/10" data-testid={`achievement-${ach.id}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#00FF88]/10 flex items-center justify-center shrink-0">
                            <Icon className="w-5 h-5 text-[#00FF88]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-outfit font-semibold text-gray-900 dark:text-white">{ach.name}</p>
                            <p className="text-xs text-gray-400 dark:text-[#52525B] font-plex">{ach.desc}</p>
                          </div>
                          <Badge className="bg-[#00FF88]/10 text-[#00FF88] border-0 text-xs font-jetbrains shrink-0">+{ach.xp}XP</Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Locked */}
            {locked.length > 0 && (
              <div>
                <p className="font-jetbrains text-[10px] tracking-[0.2em] text-gray-400 dark:text-[#52525B] uppercase mb-3">LOCKED ({locked.length})</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="locked-achievements">
                  {locked.map(ach => {
                    const Icon = ICON_MAP[ach.icon] || Star;
                    return (
                      <div key={ach.id} className="terminal-card rounded-lg p-4 opacity-50" data-testid={`achievement-locked-${ach.id}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-white/[0.04] flex items-center justify-center shrink-0">
                            <Lock className="w-5 h-5 text-gray-400 dark:text-[#52525B]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-outfit font-semibold text-gray-500 dark:text-[#A1A1AA]">{ach.name}</p>
                            <p className="text-xs text-gray-400 dark:text-[#52525B] font-plex">{ach.desc}</p>
                          </div>
                          <Badge className="bg-gray-100 dark:bg-white/5 text-gray-400 dark:text-[#52525B] border-0 text-xs font-jetbrains shrink-0">+{ach.xp}XP</Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default LeaderboardPage;
